import { useEffect, useState } from 'react';
import { Search, Shield } from 'lucide-react';
import { authFetch } from '../services/api';

function formatDateTime(s) {
  return new Date(s).toLocaleString('en-GB');
}

export default function AdminDocumentAccessLog() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({
    investor_email: '',
    broker_email: '',
    document_id: '',
  });

  const load = async () => {
    setRows(null);
    const params = new URLSearchParams();
    const findId = async (email) => {
      if (!email) return null;
      const r = await authFetch(`/api/admin/users?email=${encodeURIComponent(email)}`);
      if (!r.ok) return null;
      const list = await r.json();
      return list[0]?.id ?? null;
    };
    const investor_id = await findId(filters.investor_email);
    const broker_id = await findId(filters.broker_email);
    if (investor_id) params.set('investor_id', investor_id);
    if (broker_id) params.set('broker_id', broker_id);
    if (filters.document_id) params.set('document_id', filters.document_id);

    const res = await authFetch(`/api/admin/document-access?${params.toString()}`);
    setRows(res.ok ? await res.json() : []);
  };

  useEffect(() => { load(); }, []);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center">
          <Shield size={20} className="text-rose-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Document access log</h1>
          <p className="text-slate-400 text-sm">
            Every broker view/download of an investor's identity-vault document.
            Append-only. Use this when investigating an investor complaint.
          </p>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="email"
          placeholder="Investor email"
          value={filters.investor_email}
          onChange={set('investor_email')}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
        />
        <input
          type="email"
          placeholder="Broker email"
          value={filters.broker_email}
          onChange={set('broker_email')}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
        />
        <input
          type="number"
          placeholder="Document ID"
          value={filters.document_id}
          onChange={set('document_id')}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
        />
        <button
          onClick={load}
          className="flex items-center justify-center gap-1 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 text-sm font-medium"
        >
          <Search size={14} /> Filter
        </button>
      </div>

      {rows === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-slate-500">No matching access events.</p>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/50 text-xs text-slate-400 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Broker</th>
                <th className="px-3 py-2 text-left">Investor</th>
                <th className="px-3 py-2 text-left">Document</th>
                <th className="px-3 py-2 text-left">Category</th>
                <th className="px-3 py-2 text-left">Action</th>
                <th className="px-3 py-2 text-left">IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-800">
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{formatDateTime(r.accessed_at)}</td>
                  <td className="px-3 py-2 text-white">{r.broker_name} <span className="text-slate-500 text-xs">({r.broker_email})</span></td>
                  <td className="px-3 py-2 text-white">{r.investor_name} <span className="text-slate-500 text-xs">({r.investor_email})</span></td>
                  <td className="px-3 py-2 text-slate-300 truncate max-w-[200px]">{r.document_filename}</td>
                  <td className="px-3 py-2 text-slate-400">{r.document_category}</td>
                  <td className="px-3 py-2 text-slate-400">{r.access_type}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{r.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
